/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
// BASE Components
import BASESvgIcon from '../../../../@BASE/core/BASESvgIcon';
// Material UI
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Typography from '@mui/material/Typography';
import { lighten } from '@mui/material/styles';
// actions
import { getInstanceCommentsCounts, getInstanceIMICommentsCounts } from '../../../actions';
// custom components
import ContentListItemHeader from './ContentListItemHeader';
import ContentListItemImageHeader from './ContentListItemImageHeader';
import ContentListItemContent from './ContentListItemContent';
import ContentListItemContentCompressed from './ContentListItemContentCompressed';
import ProgressIndicators from '../../../shared-components/cards/snapshots/ProgressIndicators';
import CommentsOverview from '../../../shared-components/cards/snapshots/CommentsOverview';

function ContentListItem({ instance, resource }) {
  const dispatch = useDispatch();
  const [commentSummary, setCommentSummary] = useState({
    pending: 0,
    approved: 0,
    disapproved: 0
  });
  const [productionCommentSummary, setProductionCommentSummary] = useState({
    pending: 0,
    approved: 0,
    disapproved: 0
  });
  const [commentView, setCommentView] = useState('storyboard');

  const handleCommentViewChange = (event, newValue) => {
    setCommentView(newValue);
  };

  const getCommentCount = async () => {
    try {
      const countResponse = await dispatch(
        getInstanceCommentsCounts(resource)({
          id: instance.id,
          showInternal: 'external'
        })
      ).unwrap();
      setCommentSummary(countResponse);
    } catch (err) {
      console.log(err);
    }
  };

  const getProductionCommentCount = async () => {
    try {
      const countResponse = await dispatch(
        getInstanceIMICommentsCounts(resource)({
          id: instance.id,
          showInternal: 'external'
        })
      ).unwrap();
      setProductionCommentSummary(countResponse);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getCommentCount();
    getProductionCommentCount();
  }, []);

  const handleCardHeader = (instance, resource) => {
    switch (resource) {
      case 'courses':
        return <ContentListItemImageHeader instance={instance} />;
      default:
        return <ContentListItemHeader instance={instance} resource={resource} />;
    }
  };

  const handleCardContent = (instance, resource, commentView, handleCommentViewChange, commentSummary, productionCommentSummary) => {
    switch (resource) {
      case 'pages':
        return <ContentListItemContentCompressed instance={instance} resource={resource} commentView={commentView} handleCommentViewChange={handleCommentViewChange} commentSummary={commentSummary} productionCommentSummary={productionCommentSummary} />;
      default:
        return <ContentListItemContent instance={instance} resource={resource} commentView={commentView} handleCommentViewChange={handleCommentViewChange} commentSummary={commentSummary} productionCommentSummary={productionCommentSummary} />;
    }
  };


  return (
    <Card className="flex flex-col shadow relative">
      {handleCardHeader(instance, resource)}
      {handleCardContent(instance, resource, commentView, handleCommentViewChange, commentSummary, productionCommentSummary)}
      <CardActions
        className="items-center justify-end py-16 px-24"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? lighten(theme.palette.background.default, 0.4)
              : lighten(theme.palette.background.default, 0.03)
        }}
      >
        <Button
          to={`/${resource}/${instance.id}`}
          component={Link}
          className="px-16 min-w-128"
          variant="outlined"
          endIcon={
            <BASESvgIcon className="" size={20}>
              heroicons-solid:arrow-sm-right
            </BASESvgIcon>
          }
        >
          View
        </Button>
      </CardActions>
    </Card>
  );
}

export default ContentListItem;
